import React, { useState } from 'react';
import { useData } from '../DataProviderContext'; // Adjust the path accordingly
import { ErrorBoundary } from 'react-error-boundary'
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getJourneyDetailsValidation } from '../storage/actionItemModel';


const LifeGoals = () => {
    const { dataState, selectGoalFocus, createGoalFocus, deleteGoal } = useData();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [goalName, setGoalName] = useState('');
    const [error, setError] = useState('');
    const [creating, setCreating] = useState(false);
    const [deletingGoal, setDeletingGoal] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const handleGoalClick = (goal) => {
        selectGoalFocus(goal);
    };

    const goals = Array.isArray(dataState?.lifeGoals?.goals) ? dataState.lifeGoals.goals : [];
    const activeJourneyId = dataState?.activeGoal?.journeyId;

    if (dataState.guestJourneyStatus) return null;

    const closeDialog = () => {
        if (creating) return;
        setDialogOpen(false);
        setGoalName('');
        setError('');
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        const errors = getJourneyDetailsValidation({ goalFocus: goalName, context: '' });
        if (errors.goalFocus) {
            setError(errors.goalFocus);
            return;
        }
        setCreating(true);
        setError('');
        try {
            await createGoalFocus(goalName);
            setDialogOpen(false);
            setGoalName('');
        } catch (creationError) {
            setError(creationError?.message || 'Could not create that goal. Try again.');
        } finally {
            setCreating(false);
        }
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setDeletingGoal(null);
        setDeleteError('');
    };

    const handleDelete = async () => {
        if (!deletingGoal) return;
        setDeleting(true);
        setDeleteError('');
        try {
            await deleteGoal(deletingGoal);
            setDeletingGoal(null);
        } catch (deletionError) {
            setDeleteError(deletionError?.message || 'Could not delete this goal. Try again.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box data-testid="journey-selector-panel">
            <ErrorBoundary fallback={<div>Something went wrong at LIFE GOALS</div>}>

                <Card
                    component="section"
                    aria-labelledby="goal-focus-heading"
                    variant="outlined"
                    sx={{ borderRadius: 2, borderTop: 4, borderTopColor: 'primary.main', bgcolor: 'background.paper', boxShadow: 1 }}
                >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3.5 }, '&:last-child': { pb: { xs: 2.5, sm: 3.5 } } }}>
                        <Stack spacing={2.25}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} spacing={2}>
                                <Box>
                                    <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                                        Goal focus
                                    </Typography>
                                    <Typography id="goal-focus-heading" variant="h5" component="h2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                                        Choose the journey to work on
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: '65ch', lineHeight: 1.6 }}>
                                        Choose a journey theme to focus the board.
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setDialogOpen(true)}
                                    data-testid="new-goal"
                                    sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
                                >
                                    New goal
                                </Button>
                            </Stack>
                            <Box
                                data-testid="goal-selector"
                                sx={{
                                    display: { xs: 'grid', sm: 'flex' },
                                    gridTemplateColumns: { xs: 'minmax(0, 1fr)' },
                                    flexWrap: { sm: 'wrap' },
                                    gap: 1,
                                }}
                            >
                                {goals.map((goal, index) => {
                                    const isActive = goal.journeyId === activeJourneyId;
                                    const label = goal.sourceProvider === 'chatgpt'
                                        ? `${goal.goal} - ChatGPT`
                                        : goal.goal;
                                    const canDelete = goal.journeyId !== 'default'
                                        && goal.source
                                        && goal.source !== 'journeybot-api';
                                    return (
                                    <Chip
                                        key={goal.journeyId || goal.goal || index}
                                        label={label}
                                        clickable={true}
                                        color={isActive ? 'primary' : 'default'}
                                        variant={isActive ? 'filled' : 'outlined'}
                                        aria-pressed={isActive}
                                        data-testid={`goal-option-${goal.journeyId || index}`}
                                        data-journey-navigation="true"
                                        onClick={() => handleGoalClick(goal)}
                                        onDelete={canDelete ? () => {
                                            setDeletingGoal(goal);
                                            setDeleteError('');
                                        } : undefined}
                                        deleteIcon={canDelete ? <DeleteOutlineIcon aria-label={`Delete ${label}`} /> : undefined}
                                        sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' }, maxWidth: '100%', fontWeight: isActive ? 800 : 500 }}
                                    />
                                    );
                                })}
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                    <Box component="form" onSubmit={handleCreate}>
                        <DialogTitle>Start to become a…</DialogTitle>
                        <DialogContent>
                            <TextField
                                autoFocus
                                required
                                fullWidth
                                margin="dense"
                                label="Enter focus"
                                value={goalName}
                                onChange={(event) => {
                                    setGoalName(event.target.value);
                                    setError('');
                                }}
                                error={Boolean(error)}
                                helperText={error || 'Keep the goal focus to seven words or fewer.'}
                                inputProps={{ 'data-testid': 'new-goal-name' }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                This creates an empty journey in this browser. Cloud saving remains a separate action on the Journey Board.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDialog} disabled={creating}>Cancel</Button>
                            <Button type="submit" variant="contained" disabled={creating} data-testid="create-goal">
                                {creating ? 'Creating…' : 'Create goal'}
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>

                <Dialog open={Boolean(deletingGoal)} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
                    <DialogTitle>Delete goal?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Delete “{deletingGoal?.goal}” and its Journey data from this account?
                        </Typography>
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            {deleteError}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
                        <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-goal">
                            {deleting ? 'Deleting…' : 'Delete goal'}
                        </Button>
                    </DialogActions>
                </Dialog>

            </ErrorBoundary>
        </Box>
    )
}

export default LifeGoals;
